/**
 * Maps a sync run's skip reason (as normalized by `normalizeSyncSkipReason`
 * on the API) to the integration setting that controls it, so the Recent Runs
 * table can offer a "Configure" link next to reasons a store can act on.
 *
 * Anchors are element ids rendered in `IntegrationSettingsDrawer` /
 * `ProPublicSyncSettingsSection`; the detail page opens its Sync Behavior tab and
 * scrolls to the anchor.
 */
export const SETTINGS_ANCHORS = {
  dataSync: 'integration-setting-data-sync',
  refurbished: 'integration-setting-refurbished',
  rental: 'integration-setting-rental',
  proPublicSync: 'integration-setting-pro-public-sync',
} as const;

export type SettingsAnchor = (typeof SETTINGS_ANCHORS)[keyof typeof SETTINGS_ANCHORS];

export interface SkipReasonAction {
  /** Link text, e.g. "Allow Pro products". */
  label: string;
  /** Settings element to scroll to. */
  anchor: SettingsAnchor;
}

const RULES: Array<{ test: RegExp; action: SkipReasonAction; platforms?: string[] }> = [
  // The Pro public sync toggle is only rendered for these platforms (see ProPublicSyncSettingsSection).
  {
    test: /pro-only product/i,
    action: { label: 'Allow Pro products', anchor: SETTINGS_ANCHORS.proPublicSync },
    platforms: ['shopify', 'bigcommerce', 'lightspeed', 'lightspeed_ecom'],
  },
  { test: /refurbished product excluded/i, action: { label: 'Include refurbished', anchor: SETTINGS_ANCHORS.refurbished } },
  { test: /rental\W*(loaner )?product excluded/i, action: { label: 'Include rentals', anchor: SETTINGS_ANCHORS.rental } },
  { test: /product sync is disabled/i, action: { label: 'Turn on product sync', anchor: SETTINGS_ANCHORS.dataSync } },
];

/** The setting that controls each catalog-gate skip code (scan Apply results, link runs). */
const GATE_CODE_ANCHORS: Record<string, SettingsAnchor> = {
  pro_only_excluded: SETTINGS_ANCHORS.proPublicSync,
  refurbished_excluded: SETTINGS_ANCHORS.refurbished,
  rental_excluded: SETTINGS_ANCHORS.rental,
};

export function settingsAnchorForGateCode(code: string): SettingsAnchor | null {
  return GATE_CODE_ANCHORS[code] ?? null;
}

/** Query parameter the detail page reads to open Sync Behavior scrolled to a setting. */
export const SETTINGS_ANCHOR_PARAM = 'anchor';

export function settingsAnchorFromParam(value: string | null): SettingsAnchor | null {
  return (Object.values(SETTINGS_ANCHORS) as string[]).includes(value ?? '') ? (value as SettingsAnchor) : null;
}

/** Link to an integration's Sync Behavior tab, scrolled to the setting. */
export function syncBehaviorSettingUrl(integrationId: string, anchor: SettingsAnchor): string {
  return `/app/integrations/${integrationId}?tab=sync-behavior&${SETTINGS_ANCHOR_PARAM}=${anchor}`;
}

export function skipReasonAction(reason: string, platform?: string): SkipReasonAction | null {
  for (const rule of RULES) {
    if (!rule.test.test(reason)) continue;
    if (rule.platforms && (!platform || !rule.platforms.includes(platform))) return null;
    return rule.action;
  }
  return null;
}
